import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n";

export const QuoteModal = ({ open, onClose }) => {
  const { t, lang } = useLanguage();
  const [volume, setVolume] = useState(100);
  const [form, setForm] = useState({ name: "", email: "", product: "" });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, volume, lang }),
      });
      if (!res.ok) throw new Error("request failed");
      toast.success(t.toasts.quote);
      setForm({ name: "", email: "", product: "" });
      setVolume(100);
      onClose();
    } catch {
      toast.error(t.toasts.error);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-[#062316] outline-none transition-colors duration-300 placeholder:text-slate-400 focus:border-[#48e38e] focus:bg-white";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="quote-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-[#04120b]/80 p-4 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="gradient-border w-full max-w-lg rounded-3xl bg-white p-8 shadow-2xl sm:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-2xl font-bold tracking-tight text-[#062316]" data-testid="quote-modal-title">
                  {t.quote.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{t.quote.sub}</p>
              </div>
              <button
                data-testid="quote-modal-close"
                onClick={onClose}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-[#48e38e] hover:text-[#062316]"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={submit} className="mt-7 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  data-testid="quote-form-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t.quote.name}
                  className={inputCls}
                />
                <input
                  data-testid="quote-form-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={t.quote.email}
                  className={inputCls}
                />
              </div>
              <select
                data-testid="quote-form-product"
                required
                value={form.product}
                onChange={(e) => setForm({ ...form, product: e.target.value })}
                className={`${inputCls} ${form.product ? "" : "text-slate-400"}`}
              >
                <option value="" disabled>
                  {t.quote.product}
                </option>
                {t.quote.products.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-medium text-[#062316]">{t.quote.volume}</label>
                  <span className="rounded-full bg-[#e9faf1] px-3 py-1 font-mono text-xs font-semibold text-[#14663d]" data-testid="quote-volume-value">
                    {volume.toLocaleString()} {t.quote.unit}
                  </span>
                </div>
                <input
                  data-testid="quote-form-volume"
                  type="range"
                  min="10"
                  max="1000"
                  step="10"
                  value={volume}
                  onChange={(e) => setVolume(Number(e.target.value))}
                  className="w-full"
                />
              </div>
              <button
                data-testid="quote-form-submit"
                type="submit"
                className="group flex w-full items-center justify-center gap-2 rounded-xl bg-[#48e38e] px-6 py-3.5 text-sm font-semibold text-[#062316] transition-all duration-300 hover:shadow-[0_0_28px_rgba(72,227,142,0.5)]"
              >
                {t.quote.submit}
                <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
